import React, { useState, useMemo } from "react";
import { GroupType } from "@src/types.generated";
import { Button } from "@src/lib/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import Text from "@src/lib/ui/Text";
import {
  InviteQrCodeDialogQuery,
  useCreateLocationInQrCodeDialogMutation,
  useInviteQrCodeDialogQuery,
} from "@src/features/app/dialogs/invitePeople/operations.generated";
import { GraphqlOperations } from "@src/types.generated";
import { useTracking } from "@src/features/app/hooks/useTracking";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import QueryStatus from "@src/features/app/QueryStatus";
import GroupSelect from "@src/features/roles/GroupSelect";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import PrintOutlineIcon from "@src/lib/icons/18px/print-outline";
import SquaresCopyOutlineIcon from "@src/lib/icons/18px/squares-copy-outline";

type Location = InviteQrCodeDialogQuery["Locations"]["objects"][0];

type Props = {
  initialLocationId?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const InviteTraineeDialog: React.FC<Props> = ({
  initialLocationId,
  open,
  onOpenChange,
}) => {
  const [locationId, setLocationId] = useState<number | null>(
    initialLocationId ?? null,
  );
  const { error, loading: queryLoading, data } = useInviteQrCodeDialogQuery();
  const locations = useMemo(() => data?.Locations.objects || [], [data]);
  const selectedLocation = useMemo(
    () => locations.find((l: Location) => l.id === locationId),
    [locations, locationId],
  );
  const { userActionPermissions } = useActionPermissions();
  const { trackEvent } = useTracking();

  const [createLocationMutation] = useCreateLocationInQrCodeDialogMutation({
    refetchQueries: [GraphqlOperations.Query.InviteQRCodeDialog],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      if (data.createLocation.location) {
        setLocationId(data.createLocation.location.id);
        trackEvent("created_location", { id: data.createLocation.location.id });
      }
    },
  });

  const createLocation = (name: string) => {
    createLocationMutation({
      variables: {
        input: { name },
      },
    });
  };

  if (!data) {
    return <QueryStatus loading={queryLoading} error={error} />;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Invite QR Code</DialogTitle>
        </DialogHeader>
        <GroupSelect
          groups={locations}
          group={selectedLocation}
          setGroup={(group) => setLocationId(group.id)}
          groupType={GroupType.Location}
          createGroup={
            userActionPermissions?.changeOrgSettings
              ? createLocation
              : undefined
          }
          dataTestId="invite-trainee-location-select"
        />
        {selectedLocation == null || selectedLocation?.signupCode == null ? (
          <div className="mt-2 flex min-h-[320px] flex-col items-center justify-center rounded-lg border border-input p-4">
            <img
              src="/images/qr-code.png"
              className="size-24"
              alt="qr-code-empty-state"
            />
            <Text
              variant="compact-description"
              className="mt-6 text-center text-muted-foreground"
            >
              Select a location
            </Text>
          </div>
        ) : (
          <div className="mt-2 flex min-h-[320px] flex-col items-center justify-center rounded-lg border border-input p-4">
            <img
              className="size-44"
              src={selectedLocation.signupCode.signupQrcodeLink}
              alt="qr-code"
            />
            <Text
              variant="compact-description"
              multiline
              className="mt-2 text-center"
            >
              Location:{" "}
              <span className="font-semibold">{selectedLocation.name}</span>
            </Text>
            <Text variant="compact-description" className="mt-1">
              Manual sign up code:{" "}
              <span className="font-semibold">
                {selectedLocation.signupCode.signupLink.split("/").pop()}
              </span>
            </Text>
            <div className="mt-4 flex justify-center gap-2 self-stretch">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex-1">
                    <PrintOutlineIcon className="mr-2 size-4" />
                    Print as flyer
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="dropdown-menu-content-w-full">
                  <DropdownMenuItem
                    onClick={() => {
                      window.open(`/group/${selectedLocation.id}/poster`);
                    }}
                  >
                    English
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      window.open(
                        `/group/${selectedLocation.id}/spanish-poster`,
                      );
                    }}
                  >
                    Spanish
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  navigator.clipboard.writeText(
                    selectedLocation.signupCode!.signupLink,
                  );
                }}
              >
                <SquaresCopyOutlineIcon className="mr-2 size-4" />
                Copy as URL
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InviteTraineeDialog;
